import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ExecutionStep } from '../types/protocol';

interface ExecutionProgressProps {
  steps: ExecutionStep[];
}

function StepStatusIcon({ status }: { status: ExecutionStep['status'] }): React.ReactElement {
  switch (status) {
    case 'success':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      );
    case 'error':
    case 'rollback_failed':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      );
    case 'running':
    case 'rolling_back':
      return (
        <svg className="sp-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
      );
    case 'rolled_back':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="1 4 1 10 7 10" />
          <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
        </svg>
      );
    case 'pending':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <circle cx="12" cy="12" r="10" />
        </svg>
      );
  }
}

export function ExecutionProgress({ steps }: ExecutionProgressProps): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="sp-execution-progress">
      <div className="sp-execution-header">
        <span className="sp-execution-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="5 3 19 12 5 21 5 3" />
          </svg>
        </span>
        <h4 className="sp-execution-title">{t('execution.executing')}</h4>
      </div>
      <ul className="sp-execution-steps">
        {steps.map((step) => (
          <li key={step.id} className={`sp-execution-step sp-execution-${step.status}`}>
            <span className="sp-execution-step-icon"><StepStatusIcon status={step.status} /></span>
            <span className="sp-execution-step-label">{step.label}</span>
            {step.message && <span className="sp-execution-step-msg">{step.message}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
